import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function Settings() {
  return (
    <div style={{ maxWidth: 880 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em', marginBottom: 6 }}>Settings</h1>
      <p style={{ color: 'var(--gray-500)', fontSize: 14, marginBottom: 28 }}>Your account, contract templates, offer documents, email templates, buyer visibility, and workspace defaults.</p>

      <AccountSection />
      <ContractTemplates />
      <DocumentLibrary />
      <EmailTemplates />
      <BuyerVisibility />
      <WorkspaceInfo />
      <MlsIntegration />
      <DocusignIntegration />
      <GmailIntegration />
    </div>
  );
}

// ── Integrations: connect the org's own MLS feed (Spark / ARMLS) ────────────────
function MlsIntegration() {
  const { role } = useAuth();
  const isOwner = role === 'owner';
  const [st, setSt]           = useState(null);   // { connected, provider, has_*, expires_at, connected_at }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [notice, setNotice]   = useState(null);
  const [test, setTest]       = useState(null);   // result of /mls/test
  const [sync, setSync]       = useState(null);   // result of /mls/sync
  const [syncing, setSyncing] = useState(false);
  const [form, setForm]       = useState({ client_id: '', client_secret: '', access_token: '', refresh_token: '' });

  const load = () => fetch('/api/integrations/mls/status', { credentials: 'include' })
    .then(r => r.json()).then(d => { setSt(d); setLoading(false); })
    .catch(() => { setSt({ connected: false }); setLoading(false); });
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const connect = async () => {
    setBusy(true); setNotice(null); setTest(null);
    try {
      const r = await fetch('/api/integrations/mls/connect', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: 'spark', ...form }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Connect failed');
      setForm({ client_id: '', client_secret: '', access_token: '', refresh_token: '' });
      setNotice({ ok: true, msg: 'MLS credentials saved.' });
      await load();
    } catch (e) { setNotice({ ok: false, msg: e.message }); } finally { setBusy(false); }
  };

  const runSync = async () => {
    setSyncing(true); setNotice(null); setSync(null);
    try {
      const r = await fetch('/api/integrations/mls/sync', { method: 'POST', credentials: 'include' });
      setSync(await r.json().catch(() => ({ ok: false, error: 'No response' })));
    } catch (e) { setSync({ ok: false, error: e.message }); } finally { setSyncing(false); }
  };

  const runTest = async () => {
    setBusy(true); setNotice(null); setTest(null);
    try {
      const r = await fetch('/api/integrations/mls/test', { method: 'POST', credentials: 'include' });
      setTest(await r.json().catch(() => ({ ok: false, error: 'No response' })));
    } catch (e) { setTest({ ok: false, error: e.message }); } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true); setNotice(null); setTest(null);
    try {
      const r = await fetch('/api/integrations/mls/disconnect', { method: 'POST', credentials: 'include' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Disconnect failed');
      await load();
    } catch (e) { setNotice({ ok: false, msg: e.message }); } finally { setBusy(false); }
  };

  const inputStyle = { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13, marginTop: 4 };
  const labelStyle = { fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <Section title="MLS Connection" sub="Connect your MLS feed (Spark / ARMLS) so feebe scans your market under your own credentials.">
      {notice && <div style={{ marginBottom: 14, fontSize: 12, color: notice.ok ? 'var(--teal)' : '#B85450' }}>{notice.msg}</div>}
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
      ) : st?.connected ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600 }}>Connected · {(st.provider || 'spark').toUpperCase()}</div>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                {[st.has_client_id && 'client id', st.has_client_secret && 'secret', st.has_refresh_token && 'refresh token', st.has_access_token && 'access token'].filter(Boolean).join(' · ')}
                {st.connected_at ? ` · since ${new Date(st.connected_at).toLocaleDateString()}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={runTest} disabled={busy || syncing} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--teal)', background: 'transparent', color: 'var(--teal)', fontSize: 12, fontWeight: 700, cursor: (busy || syncing) ? 'default' : 'pointer' }}>{busy ? 'Testing…' : 'Test connection'}</button>
              {isOwner && <button onClick={runSync} disabled={busy || syncing} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: (busy || syncing) ? 'default' : 'pointer' }}>{syncing ? 'Syncing…' : 'Sync now'}</button>}
              {isOwner && <button onClick={disconnect} disabled={busy || syncing} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #B85450', background: 'transparent', color: '#B85450', fontSize: 12, fontWeight: 700, cursor: (busy || syncing) ? 'default' : 'pointer' }}>Disconnect</button>}
            </div>
          </div>
          {test && <TestResult test={test} />}
          {sync && <SyncResult sync={sync} />}
        </>
      ) : !isOwner ? (
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>No MLS feed connected. Only the organization owner can connect one.</div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--gray-300)' }}>Enter your Spark API credentials. They're encrypted at rest and never shown again.</div>
          <div><div style={labelStyle}>Client ID</div><input style={inputStyle} value={form.client_id} onChange={set('client_id')} autoComplete="off" /></div>
          <div><div style={labelStyle}>Client Secret</div><input style={inputStyle} type="password" value={form.client_secret} onChange={set('client_secret')} autoComplete="off" /></div>
          <div><div style={labelStyle}>Access Token <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span></div><input style={inputStyle} type="password" value={form.access_token} onChange={set('access_token')} autoComplete="off" /></div>
          <div><div style={labelStyle}>Refresh Token <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span></div><input style={inputStyle} type="password" value={form.refresh_token} onChange={set('refresh_token')} autoComplete="off" /></div>
          <div>
            <button onClick={connect} disabled={busy} style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>{busy ? 'Saving…' : 'Connect MLS'}</button>
          </div>
        </div>
      )}
    </Section>
  );
}

function TestResult({ test }) {
  const ok = test && test.ok;
  return (
    <div style={{ marginTop: 14, padding: 14, borderRadius: 10, border: `1px solid ${ok ? 'var(--teal)' : '#B85450'}`, background: 'var(--navy)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: ok ? 'var(--teal)' : '#B85450', marginBottom: ok ? 8 : 0 }}>
        {ok ? `Connection OK — pulled ${test.sample_count} listing${test.sample_count === 1 ? '' : 's'} from ${test.market || 'the feed'}` : `Connection failed: ${test.error || 'unknown error'}`}
      </div>
      {ok && (
        <div style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.7 }}>
          {test.sample && <div>Sample: {test.sample.address || '—'}{test.sample.city ? `, ${test.sample.city}` : ''} · {test.sample.status || '—'} · {test.sample.list_price ? `$${Number(test.sample.list_price).toLocaleString()}` : '—'} · {test.sample.photo_count} photo{test.sample.photo_count === 1 ? '' : 's'}</div>}
          {test.photo_host && <div>Photo host: <code style={{ color: 'var(--white)' }}>{test.photo_host}</code></div>}
          {Array.isArray(test.detected_fields) && <div style={{ color: 'var(--gray-500)' }}>{test.detected_fields.length} fields returned.</div>}
        </div>
      )}
    </div>
  );
}

function SyncResult({ sync }) {
  const ok = sync && sync.ok;
  return (
    <div style={{ marginTop: 14, padding: 14, borderRadius: 10, border: `1px solid ${ok ? 'var(--teal)' : '#B85450'}`, background: 'var(--navy)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: ok ? 'var(--teal)' : '#B85450', marginBottom: ok ? 6 : 0 }}>
        {ok
          ? `Synced ${(sync.records_upserted ?? 0).toLocaleString()} listing${sync.records_upserted === 1 ? '' : 's'} from ${sync.market || 'the feed'}`
          : `Sync failed: ${sync.error || 'unknown error'}`}
      </div>
      {ok && (
        <div style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.7 }}>
          <div>{(sync.records_fetched ?? 0).toLocaleString()} fetched · {(sync.records_upserted ?? 0).toLocaleString()} kept{typeof sync.duration_seconds === 'number' ? ` · ${sync.duration_seconds.toFixed(1)}s` : ''}</div>
          <div style={{ color: 'var(--gray-500)' }}>Comps + opportunity scoring are running in the background — listings will appear under MLS Opportunities shortly.</div>
        </div>
      )}
    </div>
  );
}

// ── Integrations: connect the org's own DocuSign account (OAuth) ────────────────
function DocusignIntegration() {
  const { role } = useAuth();
  const [st, setSt]         = useState(null);   // { connected, configured, account_name, email, connected_at }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]     = useState(false);
  const [notice, setNotice] = useState(null);   // result of the OAuth round-trip (?docusign=…)

  const load = () => fetch('/api/integrations/docusign/status', { credentials: 'include' })
    .then(r => r.json()).then(d => { setSt(d); setLoading(false); })
    .catch(() => { setSt({ connected: false, configured: false }); setLoading(false); });

  useEffect(() => {
    // Surface the callback's redirect result, then strip the query so a refresh doesn't repeat it.
    const p = new URLSearchParams(window.location.search);
    const r = p.get('docusign');
    if (r === 'connected') setNotice({ ok: true, msg: 'DocuSign connected.' });
    else if (r === 'error') setNotice({ ok: false, msg: 'DocuSign connection failed: ' + (p.get('reason') || 'unknown') });
    if (r) window.history.replaceState({}, '', '/settings');
    load();
  }, []);

  const connect = async () => {
    setNotice(null);
    try {
      const r = await fetch('/api/integrations/docusign/connect?json=1', { credentials: 'include' });
      const d = await r.json();
      if (!r.ok || !d.url) throw new Error(d.error || 'Could not start the DocuSign connection');
      window.location.href = d.url;
    } catch (e) { setNotice({ ok: false, msg: e.message }); }
  };
  const disconnect = async () => {
    setBusy(true); setNotice(null);
    try {
      const r = await fetch('/api/integrations/docusign/disconnect', { method: 'POST', credentials: 'include' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Disconnect failed');
      await load();
    } catch (e) { setNotice({ ok: false, msg: e.message }); } finally { setBusy(false); }
  };

  const isOwner = role === 'owner';
  return (
    <Section title="Integrations" sub="Connect DocuSign so contracts go out for signature from your own account.">
      {notice && <div style={{ marginBottom: 14, fontSize: 12, color: notice.ok ? 'var(--teal)' : '#B85450' }}>{notice.msg}</div>}
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
      ) : !st?.configured ? (
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>DocuSign connection isn't available yet — coming soon.</div>
      ) : st.connected ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600 }}>Connected{st.account_name ? ` · ${st.account_name}` : ''}</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
              {st.email || ''}{st.connected_at ? ` · since ${new Date(st.connected_at).toLocaleDateString()}` : ''}
            </div>
          </div>
          {isOwner
            ? <button onClick={disconnect} disabled={busy} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #B85450', background: 'transparent', color: '#B85450', fontSize: 12, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>{busy ? 'Disconnecting…' : 'Disconnect'}</button>
            : <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Only the owner can change this.</span>}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--gray-300)' }}>Not connected. Envelopes use the buyer-side <b>Signer Email</b> from Workspace Defaults above.</div>
          {isOwner
            ? <button onClick={connect} style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Connect DocuSign</button>
            : <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Only the owner can connect.</span>}
        </div>
      )}
    </Section>
  );
}

// ── Gmail: each person connects their OWN mailbox ──────────────────────────────
// Offers and follow-ups go out from you, replies land in your inbox, and the threads feebe started are
// synced back onto the deal. Any role can connect — this is per person, not per org.
const GMAIL_REASONS = {
  session_mismatch: 'you were signed in as someone else when Google sent you back. Sign in as yourself and try again.',
  missing_permissions: 'feebe needs both "send email" and "read email" — please tick both boxes on Google’s screen.',
  access_denied: 'Google access was not granted.',
  no_refresh_token: 'Google did not return offline access. Remove feebe at myaccount.google.com/permissions and reconnect.',
};

function GmailIntegration() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = () => fetch('/api/integrations/gmail/status', { credentials: 'include' })
    .then(r => r.json()).then(setSt)
    .catch(() => setSt({ connected: false, configured: false }));

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const r = p.get('gmail');
    if (r === 'connected') setNotice({ ok: true, msg: 'Gmail connected. You can now send offers from any deal.' });
    else if (r === 'error') setNotice({ ok: false, msg: 'Gmail connection failed: ' + (GMAIL_REASONS[p.get('reason')] || p.get('reason') || 'unknown') });
    if (r) window.history.replaceState({}, '', '/settings');
    load();
  }, []);

  const connect = async () => {
    setNotice(null);
    try {
      const r = await fetch('/api/integrations/gmail/connect?json=1', { credentials: 'include' });
      const d = await r.json();
      if (!r.ok || !d.url) throw new Error(d.error || 'Could not start the Gmail connection');
      window.location.href = d.url;
    } catch (e) { setNotice({ ok: false, msg: e.message }); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect Gmail? Replies will stop syncing to your deals until you reconnect.')) return;
    setBusy(true); setNotice(null);
    try {
      const r = await fetch('/api/integrations/gmail/disconnect', { method: 'POST', credentials: 'include' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Disconnect failed');
      await load();
    } catch (e) { setNotice({ ok: false, msg: e.message }); } finally { setBusy(false); }
  };

  return (
    <Section title="Gmail" sub="Send offers and follow-ups from your own Gmail, with contracts attached. Replies to those emails show up on the deal.">
      {notice && <div style={{ marginBottom: 14, fontSize: 12, color: notice.ok ? 'var(--teal)' : '#B85450' }}>{notice.msg}</div>}
      {!st ? (
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
      ) : !st.configured ? (
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Gmail connection isn't available yet — coming soon.</div>
      ) : st.connected ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600 }}>Connected · {st.email}</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
              {st.connected_at ? `Since ${new Date(st.connected_at).toLocaleDateString()}` : ''}
              {!st.sending_enabled && ' · Sending is switched off on this server — emails are logged, not sent.'}
              {st.stub && ' · Test mode (no real Gmail)'}
            </div>
          </div>
          <button onClick={disconnect} disabled={busy} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #B85450', background: 'transparent', color: '#B85450', fontSize: 12, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>{busy ? 'Disconnecting…' : 'Disconnect'}</button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, color: 'var(--gray-300)' }}>
            {st.status === 'revoked' ? 'Gmail access expired or was removed — reconnect to keep sending.' : 'Not connected.'}
          </div>
          <button onClick={connect} style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {st.status === 'revoked' ? 'Reconnect Gmail' : 'Connect Gmail'}
          </button>
        </div>
      )}
    </Section>
  );
}

// ── Account: email, org, current plan, and the placeholder upgrade flow ─────────
const PLAN_ORDER = ['demo', 'basic', 'pro', 'enterprise'];
const PLAN_META = {
  demo:       { name: 'Demo',       blurb: 'Guided sandbox on sample data.' },
  basic:      { name: 'Basic',      blurb: 'Solo operator — connect your own MLS.' },
  pro:        { name: 'Pro',        blurb: 'Higher limits, voicemail, AI acquisitions agent.' },
  enterprise: { name: 'Enterprise', blurb: 'Brokerage — unlimited, multiple agents.' },
};

function AccountSection() {
  const { user, org, plan, role, refresh } = useAuth();
  const [target, setTarget] = useState(null);   // plan key the user is upgrading to (opens modal)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!user) return null;

  const curIdx = PLAN_ORDER.indexOf(plan?.key);
  const higher = PLAN_ORDER.filter((_, i) => i > curIdx);
  const isOwner = role === 'owner';

  const doUpgrade = async (planKey) => {
    setBusy(true); setErr(null);
    try {
      const r = await fetch('/api/account/plan', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ plan: planKey }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Upgrade failed');
      await refresh();   // re-pull /api/auth/me → plan updates everywhere immediately
      setTarget(null);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const rowStyle = { display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderTop: '1px solid var(--navy-border)', fontSize: 13 };
  return (
    <Section title="Account" sub="Your account, organization, and current plan.">
      <div style={rowStyle}><span style={{ color: 'var(--gray-500)' }}>Email</span><span style={{ color: 'var(--white)' }}>{user.email}</span></div>
      <div style={rowStyle}><span style={{ color: 'var(--gray-500)' }}>Organization</span><span style={{ color: 'var(--white)' }}>{org?.name || '—'}</span></div>
      <div style={rowStyle}>
        <span style={{ color: 'var(--gray-500)' }}>Current plan</span>
        <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: 'var(--teal-dim)', color: 'var(--teal)' }}>{plan?.name || PLAN_META[plan?.key]?.name || plan?.key}</span>
      </div>

      <div style={{ marginTop: 16 }}>
        {!isOwner ? (
          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Only the organization owner can change the plan.</div>
        ) : higher.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--gray-300)' }}>You're on the top plan — nothing to upgrade. 🎉</div>
        ) : (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Upgrade</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {higher.map(k => (
                <button key={k} onClick={() => setTarget(k)}
                  style={{ flex: '1 1 200px', textAlign: 'left', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--navy-border)', background: 'var(--navy)', cursor: 'pointer' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>Upgrade to {PLAN_META[k].name}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 3 }}>{PLAN_META[k].blurb}</div>
                </button>
              ))}
            </div>
          </>
        )}
        {err && <div style={{ marginTop: 10, fontSize: 12, color: '#B85450' }}>{err}</div>}
      </div>

      {target && <UpgradeModal targetName={PLAN_META[target].name} busy={busy} onCancel={() => { setTarget(null); setErr(null); }} onConfirm={() => doUpgrade(target)} />}
    </Section>
  );
}

// Placeholder paywall — the real credit-card flow eventually lives here.
function UpgradeModal({ targetName, busy, onCancel, onConfirm }) {
  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(8,12,20,0.72)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--white)', marginBottom: 12 }}>Upgrade to {targetName}</div>
        <p style={{ fontSize: 14, color: 'var(--gray-300)', lineHeight: 1.6, marginBottom: 22 }}>
          This is the part where you'd put in your credit card info to upgrade your account. But looks like you're one of Billy's special friends. Lucky you —{' '}
          <a onClick={() => !busy && onConfirm()} style={{ color: 'var(--teal)', fontWeight: 700, textDecoration: 'underline', cursor: busy ? 'default' : 'pointer' }}>{busy ? 'upgrading…' : 'click here to upgrade'}</a>.
        </p>
        <div style={{ textAlign: 'right' }}>
          <button onClick={onCancel} disabled={busy} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', fontSize: 13, cursor: busy ? 'default' : 'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Descriptions for the three fixed per-org template slots (migration 032). Each org uploads its own
// PDFs — there is no shared default. After an upload, the user maps the fillable fields in the builder.
const TEMPLATE_SLOT_META = {
  frontside_purchase:   { desc: 'Your purchase contract (e.g. TREC 20-18) — sent to the listing agent.' },
  frontside_assignment: { desc: 'Assignment agreement — front side.' },
  backside_assignment:  { desc: 'Assignment agreement — back side (assignor → end buyer).' },
};

function ContractTemplates() {
  const { role } = useAuth();
  const navigate = useNavigate();
  // Since migration 080 a slot holds the caller's OWN template if they have uploaded one, else the
  // org default. Every member may upload their own — this used to be owner/admin-only, which left an
  // acquisitions agent looking at somebody else's contract with no control of any kind. The server
  // decides which row an upload writes (role-derived), so `can_edit` per row is what the UI trusts
  // rather than re-deriving the rule here.
  const isOwnerish = role === 'owner' || role === 'admin';
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState(null);
  const [error, setError]         = useState(null);

  function load() {
    fetch('/api/contracts/templates', { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setTemplates(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }

  useEffect(load, []);

  async function handleUpload(slot, file) {
    setUploading(slot);
    setError(null);
    try {
      const buffer = await file.arrayBuffer();
      const r = await fetch(`/api/contracts/templates/${encodeURIComponent(slot)}/upload?filename=${encodeURIComponent(file.name)}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/pdf' }, body: buffer,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.error) throw new Error(data.error || 'Upload failed');
      setUploading(null);
      load();   // the slot has just flipped from the org's template to this person's
      // Straight into the builder to map fields on the freshly uploaded PDF.
      navigate(`/settings/templates/${slot}`);
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
      setUploading(null);
    }
  }

  async function handleDelete(slot) {
    const t = templates.find(x => x.slot === slot);
    if (!t || !t.id) return;
    // Say what actually happens: removing your own falls back to the org's, which is not the same
    // thing as the slot going empty.
    const msg = t.scope === 'mine' && t.has_org_fallback
      ? 'Remove your template and its field mapping? This slot will fall back to your organization\u2019s template.'
      : 'Remove this template and its field mapping?';
    if (!window.confirm(msg)) return;
    setError(null);
    try {
      const r = await fetch(`/api/contracts/templates/${t.id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Delete failed');
      load();
    } catch (err) { setError(err.message); }
  }

  return (
    <Section title="Contract Templates" sub={isOwnerish
      ? 'Upload your organization\u2019s contract PDFs, then map the fields your agent fills. Every member generates from these unless they upload their own. Property data auto-fills from the MLS on MLS deals.'
      : 'Upload your own contract PDFs, then map the fields you fill. Until you upload one, you generate from your organization\u2019s template. Property data auto-fills from the MLS on MLS deals.'}>
      {loading
        ? <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '12px 0' }}>Loading templates…</div>
        : templates.filter(t => (t.kind || 'slot') === 'slot').map(t => (
            <TemplateRow key={t.slot} template={t} isOwnerish={isOwnerish} uploading={uploading === t.slot}
              onUpload={file => handleUpload(t.slot, file)}
              onEdit={() => navigate(`/settings/templates/${t.slot}`)}
              onDelete={() => handleDelete(t.slot)} />
          ))
      }
      {error && <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(184, 84, 80,0.08)', borderRadius: 6, fontSize: 12, color: '#B85450' }}>{error}</div>}
    </Section>
  );
}

function TemplateRow({ template: t, isOwnerish, uploading, onUpload, onEdit, onDelete }) {
  const fileRef = useRef(null);
  const meta = TEMPLATE_SLOT_META[t.slot] || { desc: '' };
  const ready = t.status === 'ready';
  const uploaded = t.exists;
  // `mine` = this person's own template. `orgs` = they are looking at the organization's, either
  // because they have not uploaded their own or because they are the owner maintaining it.
  const mine = t.scope === 'mine';
  const orgs = t.scope === 'org';
  // The server said whether this caller may change THIS row (own always; the org's only as
  // owner/admin). Trust it rather than re-deriving the rule — one policy, one place.
  const canEdit = t.can_edit !== false;
  // An agent looking at the org's template is not blocked: they upload their own, which is a
  // different action from "replace" and has to read as one.
  const uploadLabel = uploading ? 'Uploading…'
    : (orgs && !isOwnerish) ? 'Upload mine'
    : uploaded ? 'Replace' : 'Upload';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '14px 16px', borderRadius: 10, marginBottom: 10,
      background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
    }}>
      <div style={{ width: 38, height: 44, background: uploaded ? 'rgba(46,158,143,0.12)' : 'rgba(120,120,120,0.10)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={uploaded ? '#2E9E8F' : '#8A8A8A'} strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{t.name}</div>
          {uploaded && (
            <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: ready ? 'rgba(46,158,143,0.15)' : 'rgba(20, 80, 74,0.15)', color: ready ? 'var(--teal)' : '#B08D57', fontWeight: 700, letterSpacing: '0.04em' }}>
              {ready ? `${t.field_count} FIELD${t.field_count === 1 ? '' : 'S'}` : 'NEEDS FIELDS'}
            </span>
          )}
          {/* Whose template this is. The single most important thing on the row: an agent has to
              know whether the contract they are about to generate is theirs or the team's. */}
          {uploaded && (
            <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, fontWeight: 700, letterSpacing: '0.04em',
              background: mine ? 'rgba(46,158,143,0.15)' : 'rgba(176,141,87,0.15)', color: mine ? 'var(--teal)' : '#B08D57' }}>
              {mine ? 'YOURS' : 'ORGANIZATION'}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{meta.desc}</div>
        <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 3 }}>
          {!uploaded
            ? <>Not uploaded yet — nothing can be generated from this slot</>
            : <>{t.filename} · {t.size_kb} KB · {t.page_count || '—'} page{t.page_count === 1 ? '' : 's'} · Modified {t.modified ? new Date(t.modified).toLocaleDateString() : '—'}</>}
        </div>
        {/* Why the buttons say what they say. Without this an agent reads "Upload mine" as a second
            copy of the same thing rather than as the contract they will actually send. */}
        <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 3 }}>
          {orgs && !isOwnerish && <>You generate from your organization&rsquo;s template. Upload your own to use your contract and your field mapping instead.</>}
          {mine && t.has_org_fallback && <>Yours — used instead of your organization&rsquo;s. Delete it to go back to theirs.</>}
          {orgs && isOwnerish && uploaded && <>Your organization&rsquo;s default. Anyone who has not uploaded their own generates from this.</>}
        </div>
      </div>
      <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) onUpload(e.target.files[0]); e.target.value = ''; }} />
      {/* Upload is offered to EVERY member — that is the whole fix. Map fields / Delete follow the
          server's can_edit, so an agent can never alter the org's template or its mapping. */}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {uploaded && canEdit && (
          <button onClick={onEdit}
            style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--teal)', background: 'transparent', color: 'var(--teal)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Map fields
          </button>
        )}
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          title={orgs && !isOwnerish ? 'Upload your own contract for this slot' : undefined}
          style={{ padding: '7px 14px', borderRadius: 6, border: `1px solid ${orgs && !isOwnerish ? 'var(--teal)' : 'var(--navy-border)'}`, background: 'transparent', color: orgs && !isOwnerish ? 'var(--teal)' : 'var(--gray-300)', fontSize: 12, fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
          {uploadLabel}
        </button>
        {uploaded && canEdit && (
          <button onClick={onDelete}
            style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: '#B85450', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

// Editable per-org workspace defaults, bound to GET/PUT /api/org-settings. A brand-new org loads
// blank fields (placeholder hints only) — it never sees another org's values.
const WS_FIELDS = [
  { key: 'buyer_entity',   label: 'Buyer / LLC',    placeholder: 'Your buyer entity / LLC' },
  { key: 'brokerage',      label: 'Brokerage',      placeholder: 'Your brokerage' },
  { key: 'title_company',  label: 'Title Company',  placeholder: 'Title company' },
  { key: 'title_contact',  label: 'Title Contact',  placeholder: 'Escrow officer / contact' },
  { key: 'escrow_address', label: 'Escrow Address', placeholder: 'Escrow office address' },
  { key: 'escrow_phone',   label: 'Escrow Phone',   placeholder: '(512) 000-0000' },
  { key: 'market_label',   label: 'Market',         placeholder: 'e.g. Austin, TX' },
  { key: 'market_city',    label: 'Market City',    placeholder: 'e.g. Austin' },
  // Deadlines count down in this zone for everyone in the org — an option period on an Austin
  // property ends at 5pm Austin time whoever is looking at it.
  { key: 'market_timezone', label: 'Market Timezone', placeholder: 'e.g. America/Chicago or America/Phoenix' },
  { key: 'market_county',  label: 'Market County',  placeholder: 'e.g. Travis' },
  { key: 'signer_email',   label: 'Signer Email',   placeholder: 'Email for your buyer-side e-signature' },
  // Buyer contact block (your entity's mailing address + contact) — used on generated contracts
  { key: 'buyer_address',  label: 'Buyer Street',   placeholder: 'Buyer mailing street' },
  { key: 'buyer_city',     label: 'Buyer City',     placeholder: 'e.g. Austin' },
  { key: 'buyer_state',    label: 'Buyer State',    placeholder: 'TX' },
  { key: 'buyer_zip',      label: 'Buyer ZIP',      placeholder: '78725' },
  { key: 'buyer_email',    label: 'Buyer Email',    placeholder: 'buyer@yourco.com' },
  { key: 'buyer_phone',    label: 'Buyer Phone',    placeholder: '(512) 000-0000' },
  // Buyer-side associate (your agent named on the contract)
  { key: 'associate_name',  label: 'Associate Name',  placeholder: 'Your agent on the contract' },
  { key: 'associate_email', label: 'Associate Email', placeholder: 'agent@yourco.com' },
  { key: 'associate_phone', label: 'Associate Phone', placeholder: '(512) 000-0000' },
  // Escrow email + brokerage/associate license & MLS numbers (used on the AZ purchase contract)
  { key: 'escrow_email',      label: 'Escrow Email',      placeholder: 'escrow@titleco.com' },
  { key: 'brokerage_license', label: 'Brokerage License #', placeholder: 'Brokerage license number' },
  { key: 'brokerage_mls_id',  label: 'Brokerage MLS #',   placeholder: 'Brokerage MLS ID' },
  { key: 'associate_license', label: 'Associate License #', placeholder: 'Agent license number' },
  { key: 'associate_mls_id',  label: 'Associate MLS #',   placeholder: 'Agent MLS ID' },
  { key: 'initials',          label: 'Initials',          placeholder: 'e.g. TL — stamped on disclosure lines' },
];

function WorkspaceInfo() {
  const blank = Object.fromEntries(WS_FIELDS.map(f => [f.key, '']));
  const [form, setForm]       = useState(blank);
  const [custom, setCustom]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [err, setErr]         = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/org-settings', { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        const next = {};
        for (const f of WS_FIELDS) next[f.key] = d?.[f.key] ?? '';
        setForm(next);
        setCustom(Array.isArray(d?.custom) ? d.custom : []);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setErr('Could not load settings'); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const update = (k, v) => { setForm(prev => ({ ...prev, [k]: v })); setSaved(false); };

  const save = async () => {
    setSaving(true); setErr(null); setSaved(false);
    try {
      const r = await fetch('/api/org-settings', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Save failed');
      setSaved(true);
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  return (
    <Section title="Workspace Defaults" sub="Used when generating contracts and outreach emails.">
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {WS_FIELDS.map(f => (
              <WsField key={f.key} label={f.label} placeholder={f.placeholder}
                value={form[f.key]} onChange={v => update(f.key, v)} />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18 }}>
            <button onClick={save} disabled={saving}
              style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save defaults'}
            </button>
            {saved && <span style={{ fontSize: 12, color: 'var(--teal)', fontWeight: 600 }}>Saved ✓</span>}
            {err && <span style={{ fontSize: 12, color: '#B85450' }}>{err}</span>}
          </div>

          <CustomDefaults rows={custom} setRows={setCustom} />
        </>
      )}
    </Section>
  );
}

// User-defined labeled workspace defaults (migration 052). Each row is persisted immediately via the
// /api/org-settings/custom endpoints (add on button, rename/re-value on blur, delete on ✕), so they
// appear in the contract template builder's "bind to a workspace default" dropdown as `custom:<id>`.
function CustomDefaults({ rows, setRows }) {
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState(null);

  const add = async () => {
    const l = label.trim();
    if (!l) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch('/api/org-settings/custom', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ label: l, value }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Could not add');
      setRows([...rows, d]); setLabel(''); setValue('');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const saveRow = async (id, patch) => {
    setErr(null);
    try {
      const r = await fetch(`/api/org-settings/custom/${id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Could not save');
      setRows(rows.map(x => x.id === id ? d : x));
    } catch (e) { setErr(e.message); }
  };

  const remove = async (id) => {
    setErr(null);
    try {
      const r = await fetch(`/api/org-settings/custom/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Could not delete'); }
      setRows(rows.filter(x => x.id !== id));
    } catch (e) { setErr(e.message); }
  };

  const patchLocal = (id, patch) => setRows(rows.map(x => x.id === id ? { ...x, ...patch } : x));
  const inp = { padding: '8px 11px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13, outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ marginTop: 26, borderTop: '1px solid var(--navy-border)', paddingTop: 18 }}>
      <div style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Custom Defaults</div>
      <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 14 }}>Your own labeled values — they appear in the contract template builder's “bind to a workspace default” dropdown.</div>

      {rows.map(row => (
        <div key={row.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, marginBottom: 10, alignItems: 'center' }}>
          <input style={inp} value={row.label || ''} placeholder="Label"
            onChange={e => patchLocal(row.id, { label: e.target.value })}
            onBlur={e => { const v = e.target.value.trim(); if (v && v !== undefined) saveRow(row.id, { label: v, value: row.value }); }} />
          <input style={inp} value={row.value || ''} placeholder="Value"
            onChange={e => patchLocal(row.id, { value: e.target.value })}
            onBlur={e => saveRow(row.id, { label: row.label, value: e.target.value })} />
          <button onClick={() => remove(row.id)} title="Delete"
            style={{ background: 'none', border: '1px solid var(--navy-border)', color: '#B85450', borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer' }}>✕</button>
        </div>
      ))}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, marginTop: 6, alignItems: 'center' }}>
        <input style={inp} value={label} placeholder="New label (e.g. Escrow officer)" onChange={e => setLabel(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }} />
        <input style={inp} value={value} placeholder="Value" onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }} />
        <button onClick={add} disabled={busy || !label.trim()}
          style={{ background: 'var(--navy)', border: '1px solid var(--teal)', color: 'var(--teal)', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: busy || !label.trim() ? 'default' : 'pointer', opacity: busy || !label.trim() ? 0.5 : 1 }}>Add</button>
      </div>
      {err && <div style={{ fontSize: 12, color: '#B85450', marginTop: 10 }}>{err}</div>}
    </div>
  );
}

function WsField({ label, value, placeholder, onChange }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <input
        value={value || ''}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 11px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13, outline: 'none' }}
      />
    </label>
  );
}

// ── Document library (migration 081) ────────────────────────────────────────────
// Reusable attachments an agent sends WITH an offer — proof of funds, agency disclosure, an
// addendum. Uploaded once here, labeled by the person who uploaded it, then ticked on in the
// compose modal's "Add additional documents".
//
// Personal by default. Only an org owner/admin sees the "Share with organization" control, because
// a proof of funds is a statement about one person's money and must not become a checkbox on
// somebody else's offer by default. The server enforces it; this only hides the control.
const LIB_ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt';
const fmtKB = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

function DocumentLibrary() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const isOwnerish = role === 'owner' || role === 'admin';
  const fileRef = useRef(null);
  const [docs, setDocs]         = useState([]);   // static files (library_documents)
  const [mapped, setMapped]     = useState([]);   // mappable templates (contract_templates kind=supplemental)
  const [canPublish, setCanPub] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [uploading, setUp]      = useState(false);
  const [error, setError]       = useState(null);
  const [pending, setPending]   = useState(null);   // { file } — awaiting a label before it uploads
  const [label, setLabel]       = useState('');
  const [share, setShare]       = useState(false);
  const [needsFill, setNeedsFill] = useState(false);

  function load() {
    Promise.all([
      fetch('/api/library-documents', { credentials: 'include' }).then(r => r.json()),
      fetch('/api/contracts/templates', { credentials: 'include' }).then(r => r.json()),
    ]).then(([lib, tpls]) => {
      setDocs(Array.isArray(lib?.documents) ? lib.documents : []);
      setCanPub(!!lib?.can_publish);
      setMapped(Array.isArray(tpls) ? tpls.filter(t => t.kind === 'supplemental') : []);
      setLoading(false);
    }).catch(e => { setError(e.message); setLoading(false); });
  }
  useEffect(load, []);

  // The label is asked for BEFORE the bytes go up: the whole point of the library is that a
  // checkbox reads "Agency disclosure" rather than "AZ_BBA_2026_final(3).pdf".
  function pick(file) {
    setError(null);
    setPending({ file });
    setLabel(file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').slice(0, 80));
    setShare(false);
    setNeedsFill(false);
  }

  async function upload() {
    if (!pending) return;
    setUp(true); setError(null);
    try {
      const file = pending.file;
      if (needsFill) {
        // A mappable offer document: a contract template in everything but name, so it goes through
        // the template path and straight into the same field builder the purchase contract uses.
        const qs = new URLSearchParams({ label: label.trim() || file.name, filename: file.name });
        const r = await fetch(`/api/contracts/offer-documents?${qs}`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/pdf' }, body: await file.arrayBuffer(),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok || d.error) throw new Error(d.error || 'Upload failed');
        setPending(null); setLabel(''); setUp(false);
        navigate(`/settings/templates/${d.slot}`);   // map its fields now — it is useless unmapped
        return;
      }
      const qs = new URLSearchParams({ label: label.trim() || file.name, filename: file.name });
      if (share) qs.set('shared', '1');
      const r = await fetch(`/api/library-documents?${qs}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: await file.arrayBuffer(),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) throw new Error(d.error || 'Upload failed');
      setPending(null); setLabel(''); setShare(false);
      load();
    } catch (e) { setError(e.message); } finally { setUp(false); }
  }

  async function rename(doc) {
    const next = window.prompt('Label for this document', doc.label);
    if (next == null || next.trim() === '' || next === doc.label) return;
    setError(null);
    try {
      const r = await fetch(`/api/library-documents/${doc.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ label: next.trim() }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Rename failed');
      load();
    } catch (e) { setError(e.message); }
  }

  async function renameMapped(t) {
    const next = window.prompt('Name for this document', t.name);
    if (next == null || next.trim() === '' || next === t.name) return;
    setError(null);
    try {
      const r = await fetch(`/api/contracts/offer-documents/${t.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: next.trim() }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Rename failed');
      load();
    } catch (e) { setError(e.message); }
  }

  async function toggleShare(doc) {
    setError(null);
    try {
      const r = await fetch(`/api/library-documents/${doc.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ shared_org: !doc.shared_org }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Update failed');
      load();
    } catch (e) { setError(e.message); }
  }

  async function remove(doc) {
    if (!window.confirm(`Remove "${doc.label}"? It stays attached to emails already sent.`)) return;
    setError(null);
    try {
      const r = await fetch(`/api/library-documents/${doc.id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Delete failed');
      load();
    } catch (e) { setError(e.message); }
  }

  async function removeMapped(t) {
    if (!window.confirm(`Remove "${t.name}" and its field mapping? Documents already generated stay on their deals.`)) return;
    setError(null);
    try {
      const r = await fetch(`/api/contracts/templates/${t.id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Delete failed');
      load();
    } catch (e) { setError(e.message); }
  }

  const pill = (bg, color, text) => (
    <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: bg, color, fontWeight: 700, letterSpacing: '0.04em' }}>{text}</span>
  );

  return (
    <Section title="Offer Documents"
      sub="Everything that can ride along with an offer. Tick the ones you want under “Add additional documents” when you email an agent — the contract, a disclosure and a proof of funds all go out in one email.">
      {loading ? (
        <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '12px 0' }}>Loading documents…</div>
      ) : (
        <>
          {docs.length === 0 && mapped.length === 0 && !pending && (
            <div style={{ fontSize: 12.5, color: 'var(--gray-500)', padding: '4px 0 14px' }}>
              No documents yet. Add a proof of funds (sent as-is) or an agency disclosure (filled with each property&rsquo;s details).
            </div>
          )}

          {/* Mappable documents first: they are the ones that need attention, because an unmapped
              one cannot be generated and will refuse the send that asks for it. */}
          {mapped.map(t => {
            const ready = t.status === 'ready';
            return (
              <div key={`t${t.id}`} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, marginBottom: 8, background: 'var(--navy-card)', border: `1px solid ${ready ? 'var(--navy-border)' : 'rgba(176,141,87,0.5)'}` }}>
                <div style={{ width: 32, height: 38, background: 'rgba(46,158,143,0.12)', borderRadius: 6, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#2E9E8F" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{t.name}</div>
                    {pill('rgba(46,158,143,0.15)', 'var(--teal)', 'FILLS PER PROPERTY')}
                    {ready
                      ? pill('rgba(46,158,143,0.15)', 'var(--teal)', `${t.field_count} FIELD${t.field_count === 1 ? '' : 'S'}`)
                      : pill('rgba(176,141,87,0.15)', '#B08D57', 'NEEDS FIELDS')}
                    {t.scope === 'org' && pill('rgba(176,141,87,0.15)', '#B08D57', 'ORGANIZATION')}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 3 }}>
                    {t.filename} · {t.size_kb} KB · {t.page_count || '—'} page{t.page_count === 1 ? '' : 's'}
                  </div>
                  {!ready && (
                    <div style={{ fontSize: 10.5, color: '#B08D57', marginTop: 3 }}>
                      Map its fields before using it — an unmapped document can&rsquo;t be filled, and an offer that asks for it will be refused.
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button onClick={() => navigate(`/settings/templates/${t.slot}`)}
                    style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--teal)', background: 'transparent', color: 'var(--teal)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Map fields
                  </button>
                  {t.can_edit && <button onClick={() => renameMapped(t)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Rename</button>}
                  {t.can_edit && <button onClick={() => removeMapped(t)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: '#B85450', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Delete</button>}
                </div>
              </div>
            );
          })}

          {/* Static files — sent byte-for-byte, no mapping. */}
          {docs.map(d => (
            <div key={`d${d.id}`} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, marginBottom: 8, background: 'var(--navy-card)', border: '1px solid var(--navy-border)' }}>
              <div style={{ width: 32, height: 38, background: 'rgba(120,120,120,0.10)', borderRadius: 6, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</div>
                  {pill('rgba(120,120,120,0.14)', 'var(--gray-300)', 'SENT AS-IS')}
                  {d.shared_org && pill('rgba(176,141,87,0.15)', '#B08D57', 'ORGANIZATION')}
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 3 }}>{d.filename} · {fmtKB(d.byte_size)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <a href={`/api/library-documents/${d.id}/download`} target="_blank" rel="noreferrer"
                  style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>View</a>
                {d.can_edit && <button onClick={() => rename(d)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Rename</button>}
                {canPublish && d.can_edit && (
                  <button onClick={() => toggleShare(d)} title={d.shared_org ? 'Make this yours only' : 'Make this available to everyone in the organization'}
                    style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${d.shared_org ? '#B08D57' : 'var(--navy-border)'}`, background: 'transparent', color: d.shared_org ? '#B08D57' : 'var(--gray-300)', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {d.shared_org ? 'Unshare' : 'Share with org'}
                  </button>
                )}
                {d.can_edit && <button onClick={() => remove(d)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: '#B85450', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Delete</button>}
              </div>
            </div>
          ))}

          {/* Label-then-upload. The "fills per property" question is asked HERE, before the bytes go
              up, because it decides which store the document lands in and there is no migrating it
              afterwards — a static row has no fields to map. */}
          {pending ? (
            <div style={{ padding: '14px 16px', borderRadius: 10, background: 'rgba(46,158,143,0.06)', border: '1px solid var(--teal)', marginTop: 4 }}>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 8 }}>{pending.file.name} · {fmtKB(pending.file.size)}</div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</label>
              <input autoFocus value={label} onChange={e => setLabel(e.target.value)} placeholder="Agency disclosure"
                onKeyDown={e => { if (e.key === 'Enter' && label.trim()) upload(); }}
                style={{ width: '100%', marginTop: 6, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13 }} />

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontSize: 12, color: 'var(--gray-300)', cursor: pending.file.type === 'application/pdf' ? 'pointer' : 'not-allowed', opacity: pending.file.type === 'application/pdf' ? 1 : 0.5 }}>
                <input type="checkbox" checked={needsFill} disabled={pending.file.type !== 'application/pdf'}
                  onChange={e => { setNeedsFill(e.target.checked); if (e.target.checked) setShare(false); }} style={{ marginTop: 2 }} />
                <span>
                  This document needs the property&rsquo;s details filled in (address, price, dates)
                  <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 2 }}>
                    {pending.file.type === 'application/pdf'
                      ? 'You’ll map its fields next, the same way as the purchase contract. A fresh copy is generated for each deal when you send.'
                      : 'Only PDFs can be filled — this file will be sent as-is.'}
                  </div>
                </span>
              </label>

              {canPublish && !needsFill && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, color: 'var(--gray-300)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={share} onChange={e => setShare(e.target.checked)} />
                  Share with the whole organization — everyone can attach it to their offers
                </label>
              )}
              {needsFill && isOwnerish && (
                <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 10 }}>
                  Uploaded as your organization&rsquo;s version — every member uses it unless they add their own.
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={upload} disabled={uploading || !label.trim()}
                  style={{ padding: '8px 16px', borderRadius: 7, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: (uploading || !label.trim()) ? 'default' : 'pointer', opacity: (uploading || !label.trim()) ? 0.6 : 1 }}>
                  {uploading ? 'Uploading…' : needsFill ? 'Upload & map fields' : 'Save to library'}
                </button>
                <button onClick={() => { setPending(null); setLabel(''); }} disabled={uploading}
                  style={{ padding: '8px 14px', borderRadius: 7, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <input ref={fileRef} type="file" accept={LIB_ACCEPT} style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) pick(e.target.files[0]); e.target.value = ''; }} />
              <button onClick={() => fileRef.current?.click()}
                style={{ marginTop: 6, padding: '8px 16px', borderRadius: 7, border: '1px solid var(--teal)', background: 'transparent', color: 'var(--teal)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                + Add a document
              </button>
            </>
          )}
        </>
      )}
      {error && <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(184, 84, 80,0.08)', borderRadius: 6, fontSize: 12, color: '#B85450' }}>{error}</div>}
    </Section>
  );
}

// ── Email templates (migration 081) ─────────────────────────────────────────────
// The body an agent uses when sending an offer, written once and picked from the compose modal.
// Same ownership model as the document library: personal, publishable by owner/admin.
//
// A template is inert text. Nothing sends it — the picker drops it into the compose box and a person
// still presses Send, which is the rule the whole Gmail integration is built on.
function EmailTemplates() {
  const [items, setItems]       = useState([]);
  const [canPublish, setCanPub] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [editing, setEditing]   = useState(null);   // { id?, name, subject, body, shared_org }
  const [saving, setSaving]     = useState(false);

  function load() {
    fetch('/api/email-templates', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setItems(Array.isArray(d?.templates) ? d.templates : []); setCanPub(!!d?.can_publish); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }
  useEffect(load, []);

  const blank = { name: '', subject: '', body: '', shared_org: false };

  async function save() {
    if (!editing?.name?.trim() || !editing?.body?.trim()) return;
    setSaving(true); setError(null);
    try {
      const isNew = !editing.id;
      const r = await fetch(isNew ? '/api/email-templates' : `/api/email-templates/${editing.id}`, {
        method: isNew ? 'POST' : 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editing.name.trim(), subject: editing.subject || '', body: editing.body,
          ...(canPublish ? { shared_org: !!editing.shared_org } : {}),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) throw new Error(d.error || 'Save failed');
      setEditing(null);
      load();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  async function remove(t) {
    if (!window.confirm(`Delete the template “${t.name}”?`)) return;
    setError(null);
    try {
      const r = await fetch(`/api/email-templates/${t.id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Delete failed');
      load();
    } catch (e) { setError(e.message); }
  }

  const input = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13, fontFamily: 'inherit' };

  return (
    <Section title="Email Templates"
      sub="Save the emails you send often — a new-offer note, a follow-up. Pick one when you email an agent and it fills the subject and body, ready to edit before you send.">
      {loading ? (
        <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '12px 0' }}>Loading templates…</div>
      ) : (
        <>
          {items.length === 0 && !editing && (
            <div style={{ fontSize: 12.5, color: 'var(--gray-500)', padding: '4px 0 14px' }}>
              No templates yet. Save the way you normally introduce an offer and it is one click away next time.
            </div>
          )}
          {items.map(t => (
            <div key={t.id} style={{ padding: '12px 16px', borderRadius: 10, marginBottom: 8, background: 'var(--navy-card)', border: '1px solid var(--navy-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{t.name}</div>
                    {t.shared_org && <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: 'rgba(176,141,87,0.15)', color: '#B08D57', fontWeight: 700, letterSpacing: '0.04em' }}>ORGANIZATION</span>}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.subject ? <><strong style={{ fontWeight: 600 }}>{t.subject}</strong> · </> : null}{(t.body || '').replace(/\s+/g, ' ').slice(0, 90)}…
                  </div>
                </div>
                {t.can_edit && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={() => setEditing({ ...t })} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => remove(t)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: '#B85450', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {editing ? (
            <div style={{ padding: '14px 16px', borderRadius: 10, background: 'rgba(46,158,143,0.06)', border: '1px solid var(--teal)', marginTop: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Template name</label>
              <input autoFocus value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}
                placeholder="New offer template" style={{ ...input, marginTop: 6, marginBottom: 12 }} />

              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subject <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: 'var(--gray-500)' }}>— optional; leave blank for replies</span></label>
              <input value={editing.subject || ''} onChange={e => setEditing({ ...editing, subject: e.target.value })}
                placeholder="Offer for {{address}}" style={{ ...input, marginTop: 6, marginBottom: 12 }} />

              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email body</label>
              <textarea value={editing.body} onChange={e => setEditing({ ...editing, body: e.target.value })} rows={10}
                placeholder={'Hi {{agent_name}},\n\nI’d like to submit an offer on {{address}}. The contract is attached…'}
                style={{ ...input, marginTop: 6, lineHeight: 1.55, resize: 'vertical' }} />
              <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 6 }}>
                Placeholders filled in when you pick the template: <code>{'{{address}}'}</code> · <code>{'{{agent_name}}'}</code> · <code>{'{{city}}'}</code> · <code>{'{{zip}}'}</code> · <code>{'{{offer}}'}</code> · <code>{'{{my_name}}'}</code>
              </div>

              {canPublish && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, color: 'var(--gray-300)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!editing.shared_org} onChange={e => setEditing({ ...editing, shared_org: e.target.checked })} />
                  Share with the whole organization
                </label>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={save} disabled={saving || !editing.name.trim() || !editing.body.trim()}
                  style={{ padding: '8px 16px', borderRadius: 7, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', opacity: (saving || !editing.name.trim() || !editing.body.trim()) ? 0.6 : 1 }}>
                  {saving ? 'Saving…' : 'Save template'}
                </button>
                <button onClick={() => setEditing(null)} disabled={saving}
                  style={{ padding: '8px 14px', borderRadius: 7, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setEditing({ ...blank })}
              style={{ marginTop: 6, padding: '8px 16px', borderRadius: 7, border: '1px solid var(--teal)', background: 'transparent', color: 'var(--teal)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              + New template
            </button>
          )}
        </>
      )}
      {error && <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(184, 84, 80,0.08)', borderRadius: 6, fontSize: 12, color: '#B85450' }}>{error}</div>}
    </Section>
  );
}

// ── Who can see the buyer book (migration 083) ──────────────────────────────────
// Owner/admin only, and rendered read-only for everyone else rather than hidden: an agent should be
// able to see WHY their colleague's buyers aren't showing, without having to ask.
function BuyerVisibility() {
  const { role } = useAuth();
  const canEdit = role === 'owner' || role === 'admin';
  const [value, setValue]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [err, setErr]         = useState(null);

  useEffect(() => {
    fetch('/api/org-settings', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setValue(d?.buyer_visibility === 'shared' ? 'shared' : 'private'); setLoading(false); })
      .catch(() => { setErr('Could not load settings'); setLoading(false); });
  }, []);

  async function choose(next) {
    if (!canEdit || next === value) return;
    const prev = value;
    setValue(next); setSaving(true); setErr(null); setSaved(false);
    try {
      // Send ONLY this field. The endpoint preserves manager-only settings that are absent from the
      // body, so this cannot clobber the workspace defaults the other form owns.
      const r = await fetch('/api/org-settings', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ buyer_visibility: next }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) throw new Error(d.error || 'Save failed');
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) { setValue(prev); setErr(e.message); } finally { setSaving(false); }
  }

  const OPTIONS = [
    { v: 'private', label: 'Private to each agent',
      desc: 'Everyone sees only the buyers they own. You and any admin see the whole book.' },
    { v: 'shared', label: 'Shared across the organization',
      desc: 'Every member can see every buyer. Useful when the team works one list — and it stops two agents pitching the same investor.' },
  ];

  return (
    <Section title="Buyer Visibility" sub="Who can see the buyers in this organization. Editing a buyer, changing its status and marketing to it always stay with the buyer's owner (or you).">
      {loading ? (
        <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '12px 0' }}>Loading…</div>
      ) : (
        <>
          {OPTIONS.map(o => (
            <label key={o.v}
              onClick={() => choose(o.v)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 10, marginBottom: 8,
                background: value === o.v ? 'rgba(46,158,143,0.06)' : 'var(--navy-card)',
                border: `1px solid ${value === o.v ? 'var(--teal)' : 'var(--navy-border)'}`,
                cursor: canEdit ? 'pointer' : 'default', opacity: canEdit ? 1 : 0.75,
              }}>
              <input type="radio" name="buyer_visibility" checked={value === o.v} disabled={!canEdit || saving}
                onChange={() => choose(o.v)} style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{o.label}</div>
                <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 2, lineHeight: 1.45 }}>{o.desc}</div>
              </div>
            </label>
          ))}
          {!canEdit && (
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 4 }}>
              Only an organization owner or admin can change this.
            </div>
          )}
          {saved && <div style={{ fontSize: 12, color: 'var(--teal)', fontWeight: 600, marginTop: 6 }}>Saved ✓</div>}
          {err && <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(184, 84, 80,0.08)', borderRadius: 6, fontSize: 12, color: '#B85450' }}>{err}</div>}
        </>
      )}
    </Section>
  );
}

function Section({ title, sub, children }) {
  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 24, marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{title}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 18 }}>{sub}</div>}
      {children}
    </div>
  );
}
